/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { Stack } from '@fluentui/react/lib/Stack';
import { IToggleProps, IToggleStyles, Toggle } from '@fluentui/react/lib/Toggle';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { OptionalColumnKey, OptionalColumnVisibility } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerQnaPairsUiStore';
import { QnaMakerQnaStore } from 'src/qnaMaker/core/stores/QnaMakerQnaStore';

export type QnaMakerDetailsCommandBarProps = TableCommandBarProps<QnaMakerQnaStore> & {
    /**
     * A boolean flag indicating if there are unsaved changes.
     */
    isInDirtyState: boolean;

    /**
     * A flag indicating whether to enable rich text editor or not.
     */
    enableRichText: boolean;

    /**
     * A callback to toggle enableRichText flag.
     */
    onChangeEnableRichText: () => void;

    /**
     * A callback on save changes click.
     */
    onSave: () => void;

    /**
     * A callback on test button click.
     */
    onTest: () => void;

    /**
     * A boolean flag indicating if there are no qnas being edited.
     */
    isEditQnaPairOn: boolean;

    /**
     * A callback to edit qna.
     */
    onEdit: () => void;

    /**
     * Array of optional displayed columns.
     */
    optionalColumnsShown: OptionalColumnVisibility[];

    /**
     * A callback for changing the optional displayed columns.
     */
    onChangeOptionalColumnsShown: (key: OptionalColumnKey) => void;

    /**
     * A boolean flag indicating if there is a save operation in the background
     * to disable all edit actions.
     */
    areEditActionsDisabled: boolean;

    /**
     * Optional flag to display the command bar in loading state.
     */
    isLoading?: boolean;
};

const messages = defineMessages({
    enableRichText: {
        id: 'qnaMaker.qnaMakerDetailsCommandBar.enableRichText',
        defaultMessage: 'Enable rich text'
    },
    saveChanges: {
        id: 'qnaMaker.qnaMakerDetailsCommandBar.saveChanges',
        defaultMessage: 'Save changes'
    },
    test: {
        id: 'qnaMaker.qnaMakerDetailsCommandBar.test',
        defaultMessage: 'Test'
    },
    edit: {
        id: 'qnaMaker.qnaMakerDetailsCommandBar.edit',
        defaultMessage: 'Edit'
    },
    show: {
        id: 'qnaMaker.qnaMakerDetailsCommandBar.show',
        defaultMessage: 'Show columns'
    },
    context: {
        id: 'qnaMaker.qnaMakerDetailsCommandBar.context',
        defaultMessage: 'Context'
    },
    metadata: {
        id: 'qnaMaker.qnaMakerDetailsCommandBar.metadata',
        defaultMessage: 'Metadata'
    }
});

const theme = getScopedTheme('QnaMakerQnaCommandBar');

const toggleStyles = classNamesFunction<IToggleProps, IToggleStyles>()({
    root: { marginBottom: '0px' }
});

/**
 * Command bar rendering all available models and filter options
 */
export const QnaMakerDetailsCommandBar = observer((props: QnaMakerDetailsCommandBarProps) => {
    const {
        onSave,
        onTest,
        onEdit,
        enableRichText,
        isInDirtyState,
        isEditQnaPairOn,
        isLoading = false,
        optionalColumnsShown,
        onChangeEnableRichText,
        areEditActionsDisabled,
        onChangeOptionalColumnsShown
    } = props;

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const saveChangesButtonIdRef = React.useRef<string>(getId());
    const editButtonIdRef = React.useRef<string>(getId());
    const testButtonIdRef = React.useRef<string>(getId());
    const showColumnsButtonIdRef = React.useRef<string>(getId());

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const showColumnsMenuItems: IContextualMenuItem[] = [
        {
            key: 'context',
            text: localize(messages.context),
            onClick: _event => {
                _event.preventDefault();
                onChangeOptionalColumnsShown('context');
            },
            checked: optionalColumnsShown.find(c => c.key === 'context').isVisible,
            canCheck: true
        },
        {
            key: 'metadata',
            text: localize(messages.metadata),
            onClick: _event => {
                _event.preventDefault();
                onChangeOptionalColumnsShown('metadata');
            },
            checked: optionalColumnsShown.find(c => c.key === 'metadata').isVisible,
            canCheck: true
        }
    ];

    const items: ICommandBarItemProps[] = [
        {
            key: 'saveChanges',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || !isInDirtyState || isEditQnaPairOn || areEditActionsDisabled;

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="saveChangesButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                onClick={onSave}
                                iconProps={{ iconName: 'Save' }}
                                disabled={!canWrite || _disabled}
                                id={saveChangesButtonIdRef.current}
                                name={localize(messages.saveChanges)}
                                text={localize(messages.saveChanges)}
                                data-automation-id="saveChangesButton"
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        },
        {
            key: 'edit',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || areEditActionsDisabled;

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="editQuestionPairButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                onClick={onEdit}
                                id={editButtonIdRef.current}
                                name={localize(messages.edit)}
                                text={localize(messages.edit)}
                                disabled={!canWrite || _disabled}
                                iconProps={{ iconName: 'Edit' }}
                                data-automation-id="editQuestionPairButton"
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        },
        {
            key: 'test',
            id: testButtonIdRef.current,
            text: localize(messages.test),
            iconProps: { iconName: 'TestBeaker' },
            onClick: onTest
        },
        {
            key: 'showColumns',
            id: showColumnsButtonIdRef.current,
            text: localize(messages.show),
            iconProps: { iconName: 'Column' },
            subMenuProps: { items: showColumnsMenuItems }
        },
        {
            key: 'enableRichText',
            onRender: () => (
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8, padding: '8px' }}>
                    <LuisText size="14px" color={isLoading && theme.disabledTextColor}>
                        {localize(messages.enableRichText)}
                    </LuisText>

                    <Toggle
                        styles={toggleStyles}
                        onChange={onChangeEnableRichText}
                        checked={enableRichText}
                        ariaLabel={localize(messages.enableRichText)}
                        disabled={isLoading}
                    />
                </Stack>
            )
        }
    ];

    return <LuisCommandBar items={items} showTopBorder={false} />;
});
